<template>
  <div>
    <b-card v-if="showSection" class="mt-3">
      <b-card-header>
        <h5 class="mb-0">
          <b-badge variant="info">{{ sectionTitle }}</b-badge>
          <b-badge v-if="summary" variant="secondary" class="ml-2">
            Total: {{ summary.totalRecords }} (Old: {{ oldCount }}, New: {{ newCount }})
          </b-badge>
        </h5>
      </b-card-header>
      <b-card-body>
        <CustomLoader v-if="isLoading" :width="50" />

        <div v-if="!isLoading && hasData">
          <!-- Old Records Section -->
          <div v-if="oldRecords.length > 0" class="mb-4">
            <h6 class="mb-2">
              <b-badge variant="warning">Old {{ typeLabel }} ({{ oldRecords.length }})</b-badge>
            </h6>
            <div class="table-responsive">
              <b-table
                :items="oldRecords"
                :fields="oldFields"
                striped
                hover
                small
                responsive
                class="table-sm"
              >
                <template v-if="isExpense" slot="documentLink" slot-scope="row">
                  <div v-if="row.item.fullDocumentPath" class="d-flex align-items-center flex-wrap">
                    <a
                      v-for="(link, idx) in getDocumentLinks(row.item.fullDocumentPath)"
                      :key="idx"
                      href="javascript:void(0)"
                      @click="openSignedDocument(getFormattedDocUrl(link))"
                      class="btn btn-xs btn-outline-primary mr-1 mb-1"
                      :title="`View Document ${idx + 1}`"
                    >
                      <i class="fa fa-eye mr-1"></i>View {{ idx > 0 ? idx + 1 : '' }}
                    </a>
                  </div>
                  <span v-else class="text-muted">-</span>
                </template>
                <template v-if="!isExpense" slot="ledger" slot-scope="row">
                  {{ row.item.ledger || '-' }}
                </template>
                <template slot="amount" slot-scope="row">
                  {{ formatNumber(row.item.amount) }}
                </template>
                <template slot="selling_amt" slot-scope="row">
                  {{ formatNumber(row.item.selling_amt) }}
                </template>
                <template slot="createdBy" slot-scope="row">
                  <div>
                    <small class="text-muted">{{ row.item.createdBy || '-' }}</small>
                    <br v-if="row.item.createdByEmail" />
                    <small v-if="row.item.createdByEmail" class="text-muted">{{ row.item.createdByEmail }}</small>
                  </div>
                </template>
                <template slot="date" slot-scope="row">
                  {{ formatDate(row.item.loginTime || row.item.docAddedDate || row.item.docRecDate) }}
                </template>
                <template v-if="isExpense" slot="docAddedDate" slot-scope="row">
                  {{ formatDate(row.item.docAddedDate || row.item.docRecDate) }}
                </template>
              </b-table>
            </div>
          </div>

          <!-- New Records Section -->
          <div v-if="newRecords.length > 0">
            <h6 class="mb-2">
              <b-badge variant="success">New {{ typeLabel }} ({{ newRecords.length }})</b-badge>
            </h6>
            <div class="table-responsive">
              <b-table
                :items="newRecords"
                :fields="newFields"
                striped
                hover
                small
                responsive
                class="table-sm"
              >
                <template v-if="isExpense" slot="documentLink" slot-scope="row">
                  <div v-if="row.item.inv_doc_url" class="d-flex align-items-center">
                    <a
                      href="javascript:void(0)"
                      @click="openSignedDocument(getFormattedDocUrl(row.item.inv_doc_url))"
                      class="btn btn-xs btn-outline-primary mr-1"
                      title="View Document"
                    >
                      <i class="fa fa-eye mr-1"></i>View
                    </a>
                  </div>
                  <span v-else class="text-muted">-</span>
                </template>
                <template v-if="!isExpense" slot="ledger" slot-scope="row">
                  {{ getLedgerName(row.item) || '-' }}
                </template>
                <template slot="amount" slot-scope="row">
                  {{ formatNumber(row.item.inv_amt || row.item.amount) }}
                </template>
                <template slot="selling_amt" slot-scope="row">
                  {{ formatNumber(row.item.selling_amt) }}
                </template>
                <template v-if="isExpense" slot="nature_of_expense" slot-scope="row">
                  {{ getNatureOfExpense(row.item) || '-' }}
                </template>
                <template slot="createdBy" slot-scope="row">
                  <div>
                    <small class="text-muted">{{ getCreatedBy(row.item) || '-' }}</small>
                    <br v-if="getCreatedByEmail(row.item)" />
                    <small v-if="getCreatedByEmail(row.item)" class="text-muted">{{ getCreatedByEmail(row.item) }}</small>
                  </div>
                </template>
                <template slot="date" slot-scope="row">
                  {{ formatDate(row.item.inv_date || row.item.ape_created_at) }}
                </template>
              </b-table>
            </div>
          </div>
        </div>

        <div v-if="!isLoading && !hasData" class="text-center text-muted py-4">
          <p>No {{ typeLabel.toLowerCase() }} records found.</p>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import CustomLoader from '../../common/customLoader.vue'
import PaymentRequestService from '../../../services/PaymentRequestService.js'
import moment from 'moment'

export default {
  name: 'OldExpenseAdditionalTask',
  components: {
    CustomLoader,
  },
  props: {
    allModuleId: {
      type: [Number, String],
      required: true,
    },
    type: {
      type: String,
      required: true,
      validator: (value) => ['expense', 'additional-task'].includes(value),
    },
    showSection: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isLoading: false,
      oldRecords: [],
      newRecords: [],
      summary: null,
      jobDetails: null,
    }
  },
  computed: {
    isExpense() {
      return this.type === 'expense'
    },
    typeLabel() {
      return this.isExpense ? 'Expenses' : 'Additional Tasks'
    },
    sectionTitle() {
      return `Old & New ${this.typeLabel}`
    },
    hasData() {
      return this.oldRecords.length > 0 || this.newRecords.length > 0
    },
    oldCount() {
      if (!this.summary) return 0
      return this.isExpense
        ? (this.summary.totalOldExpenses || 0)
        : (this.summary.totalOldAdditionalTasks || 0)
    },
    newCount() {
      if (!this.summary) return 0
      return this.isExpense
        ? (this.summary.totalNewExpenses || 0)
        : (this.summary.totalNewAdditionalTasks || 0)
    },
    oldFields() {
      if (this.isExpense) {
        return [
          { key: 'docAddedDate', label: 'Date', sortable: true },
          { key: 'natureOfExpense', label: 'Nature of Expense', sortable: true },
          { key: 'documentLink', label: 'Document', sortable: false },
          { key: 'docRemarks', label: 'Remarks', sortable: true },
          { key: 'createdBy', label: 'Created By', sortable: true },
        ]
      } else {
        return [
          { key: 'loginTime', label: 'Date', sortable: true },
          { key: 'ledger', label: 'Particulars', sortable: true },
          { key: 'amount', label: 'Amount', sortable: true },
          { key: 'scCategory', label: 'Bill to Customer', sortable: true },
          { key: 'selling_amt', label: 'Selling Amount', sortable: true },
          { key: 'remark', label: 'Remarks', sortable: true },
          { key: 'createdBy', label: 'Created By', sortable: true },
        ]
      }
    },
    newFields() {
      if (this.isExpense) {
        return [
          { key: 'inv_date', label: 'Date', sortable: true },
          { key: 'nature_of_expense', label: 'Nature of Expense', sortable: true },
          { key: 'documentLink', label: 'Document', sortable: false },
          { key: 'sc_remark', label: 'Remarks', sortable: true },
          { key: 'createdBy', label: 'Created By', sortable: true },
        ]
      } else {
        return [
          { key: 'inv_date', label: 'Date', sortable: true },
          { key: 'ledger', label: 'Particulars', sortable: true },
          { key: 'amount', label: 'Amount', sortable: true },
          { key: 'sc_category', label: 'Bill to Customer', sortable: true },
          { key: 'selling_amt', label: 'Selling Amount', sortable: true },
          { key: 'sc_remark', label: 'Remarks', sortable: true },
          { key: 'createdBy', label: 'Created By', sortable: true },
        ]
      }
    },
  },
  watch: {
    allModuleId: {
      handler(newVal) {
        if (newVal) {
          this.fetchData()
        }
      },
      immediate: true,
    },
  },
  methods: {
    async fetchData() {
      const allModuleId = Number(this.allModuleId)
      if (!allModuleId || allModuleId <= 0) {
        return
      }

      this.isLoading = true
      try {
        const payload = {
          allModuleId: allModuleId,
          page: 1,
          pageSize: 100,
          isAll: true,
        }

        const response = await PaymentRequestService.getAllOldExpAdd(payload)
        const { status, data } = response || {}
        // Axios: data is the outer payload { status, message, data: {...} }
        const serverData = data && data.data ? data.data : null

        if (status === 200 && data && data.status !== false && serverData) {
          this.summary = serverData.summary || null
          this.jobDetails = serverData.jobDetails || null

          if (this.isExpense) {
            this.oldRecords = Array.isArray(serverData.oldExpenses) ? serverData.oldExpenses : []
            this.newRecords = Array.isArray(serverData.newExpenses) ? serverData.newExpenses : []
          } else {
            this.oldRecords = Array.isArray(serverData.oldAdditionalTasks) ? serverData.oldAdditionalTasks : []
            this.newRecords = Array.isArray(serverData.newAdditionalTasks) ? serverData.newAdditionalTasks : []
          }
        } else {
          this.oldRecords = []
          this.newRecords = []
          this.summary = null
        }
      } catch (error) {
        console.error('fetchOldExpenseAdditionalTask error:', error)
        this.oldRecords = []
        this.newRecords = []
        this.summary = null
      } finally {
        this.isLoading = false
      }
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '-'
      const num = Number(value)
      return isNaN(num) ? '-' : num.toFixed(2)
    },
    formatDate(value) {
      if (!value) return '-'
      return moment(value).format('DD-MMM-YYYY')
    },
    getDocumentLinks(fullPath) {
      if (!fullPath) return []
      if (typeof fullPath === 'string' && fullPath.includes(',')) {
        return fullPath.split(',').map((link) => link.trim())
      }
      return [fullPath]
    },
    getFormattedDocUrl(urlOrKey) {
      if (!urlOrKey) return ''

      // 1. Strip any query parameters if present
      let cleanPath = urlOrKey.split('?')[0]

      // 2. Extract key from full URL if it contains ASSIGNMENT_UPLOADS
      if (cleanPath.includes('ASSIGNMENT_UPLOADS/')) {
        const parts = cleanPath.split('ASSIGNMENT_UPLOADS/')
        return 'ASSIGNMENT_UPLOADS/' + parts[parts.length - 1]
      }

      // 3. For raw filenames, build the path
      if (!cleanPath.startsWith('http') && !cleanPath.includes('/')) {
        const jobNo = (this.jobDetails && this.jobDetails.JobNo) || ''
        if (jobNo) {
          const folderJobNo = jobNo.replaceAll('/', '_')
          const fileDoc = cleanPath.replaceAll('/', '_')
          return `ASSIGNMENT_UPLOADS/${folderJobNo}/${fileDoc}`
        }
      }

      return cleanPath
    },
    getCreatedBy(item) {
      if (item.createdBy) return item.createdBy
      if (item.ape_cu_relation && item.ape_cu_relation.employeeName) return item.ape_cu_relation.employeeName
      return null
    },
    getCreatedByEmail(item) {
      if (item.createdByEmail) return item.createdByEmail
      if (item.ape_cu_relation && item.ape_cu_relation.email) return item.ape_cu_relation.email
      return null
    },
    getLedgerName(item) {
      if (item.ledger) return item.ledger
      if (item.ledgermaster_relation && item.ledgermaster_relation.ledger) return item.ledgermaster_relation.ledger
      return null
    },
    getNatureOfExpense(item) {
      if (item.nature_of_expense) return item.nature_of_expense
      if (item.nature_of_expense_relation && item.nature_of_expense_relation.nature_name) {
        return item.nature_of_expense_relation.nature_name
      }
      return null
    },
  },
}
</script>

<style scoped>
.btn-xs {
  padding: 0.15rem 0.4rem;
  font-size: 0.75rem;
  line-height: 1.2;
}
</style>



// WEBPACK FOOTER //
// OldExpenseAdditionalTask.vue?351292ae